
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface PeriodTrackingFormProps {
  periodData: any;
  onSuccess: () => void;
  initialData?: {
    lastPeriodDate?: Date;
    periodDuration?: string;
    cycleLength?: string;
  };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const PeriodTrackingForm = ({ periodData, onSuccess, initialData, open, onOpenChange }: PeriodTrackingFormProps) => {
  const { toast } = useToast();
  const [lastPeriodDate, setLastPeriodDate] = useState<Date | undefined>(initialData?.lastPeriodDate);
  const [periodDuration, setPeriodDuration] = useState(initialData?.periodDuration || "");
  const [cycleLength, setCycleLength] = useState(initialData?.cycleLength || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lastPeriodDate || !periodDuration || !cycleLength) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // En cas de conflit, on met à jour les données existantes
      const { error } = await supabase
        .from("period_tracking")
        .upsert(
          {
            user_id: user.id,
            last_period_date: lastPeriodDate.toISOString(),
            period_duration: parseInt(periodDuration),
            cycle_length: parseInt(cycleLength),
          },
          {
            onConflict: 'user_id',
            ignoreDuplicates: false
          }
        );

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Vos informations ont été enregistrées",
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'enregistrement",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Informations du cycle</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lastPeriodDate">Date des dernières règles</Label>
            <div className="relative">
              <Input
                id="lastPeriodDate"
                type="date"
                value={lastPeriodDate ? lastPeriodDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setLastPeriodDate(e.target.value ? new Date(e.target.value) : undefined)}
                className="w-full"
              />
              <CalendarIcon className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="periodDuration">Durée des règles (en jours)</Label>
            <Input
              id="periodDuration"
              type="number"
              min="1"
              max="10"
              value={periodDuration}
              onChange={(e) => setPeriodDuration(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cycleLength">Durée du cycle (en jours)</Label>
            <Input
              id="cycleLength"
              type="number"
              min="21"
              max="35"
              value={cycleLength}
              onChange={(e) => setCycleLength(e.target.value)}
            />
          </div>

          <Button type="submit" disabled={isSubmitting} className="w-full">
            {periodData ? "Mettre à jour" : "Enregistrer"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
